import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Search, Eye, Play, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import api from '../services/api';
import TimeAgo from '../components/TimeAgo';
import Dialog from '../components/Dialog';
import { useDialog } from '../hooks/useDialog';
import Pagination from '../components/Pagination';
import './ExecutionHistory.css';

const ExecutionHistory = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { dialogState, closeDialog, confirm, alert } = useDialog();

    const [executions, setExecutions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Initialize state from URL params
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
    const [pagination, setPagination] = useState({
        page: parseInt(searchParams.get('page')) || 1,
        limit: parseInt(searchParams.get('limit')) || parseInt(localStorage.getItem('rova_limit_history')) || 20,
        total: 0,
        totalPages: 0
    });

    // Debounce search term (500ms delay)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Update URL when state changes
    useEffect(() => {
        const params = new URLSearchParams();

        params.set('page', pagination.page.toString());
        params.set('limit', pagination.limit.toString());

        if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
        if (statusFilter) params.set('status', statusFilter);

        setSearchParams(params, { replace: true });
    }, [pagination.page, pagination.limit, debouncedSearchTerm, statusFilter]);

    useEffect(() => {
        let isMounted = true;

        const loadExecutions = async () => {
            if (!isMounted) return;
            await fetchExecutions();
        };

        loadExecutions();

        return () => {
            isMounted = false;
        };
    }, [pagination.page, pagination.limit, debouncedSearchTerm, statusFilter]);

    const fetchExecutions = async () => {
        try {
            setLoading(true);
            const params = {
                limit: pagination.limit,
                page: pagination.page
            };

            if (statusFilter) params.status = statusFilter;
            if (debouncedSearchTerm) params.search = debouncedSearchTerm;

            const response = await api.get('/tests/executions', { params });
            const { executions: fetchedExecutions, pagination: serverPagination } = response.data;

            setExecutions(fetchedExecutions || []);

            // Update pagination from server response
            setPagination(prev => ({
                ...prev,
                total: serverPagination.total,
                totalPages: serverPagination.totalPages
            }));
        } catch (error) {
            console.error('Error fetching executions:', error);
            await alert('Failed to load execution history. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRerun = async (execution, e) => {
        e.stopPropagation();

        if (!execution.testGoalId) {
            await alert('Cannot rerun this execution: No associated test goal found.');
            return;
        }

        try {
            const response = await api.post(`/tests/goals/${execution.testGoalId}/rerun`);
            navigate(`/test/execution/${response.data.execution.id}`);
        } catch (error) {
            console.error('Error rerunning execution:', error);
            const errorMessage = error.response?.data?.error || 'Failed to rerun execution.'
            let displayMessage = errorMessage;
            if (errorMessage.toLowerCase().includes('quota exceeded')) {
                displayMessage = (
                    <span>
                        {errorMessage}.{' '}
                        <Link to="/settings/billing" style={{ textDecoration: 'underline' }}>
                            Upgrade
                        </Link>
                    </span>
                );
            }
            await alert({
                title: 'Error',
                message: displayMessage,
                variant: 'danger'
            });
        }
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleLimitChange = (newLimit) => {
        setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            queued: { label: 'queued', className: 'status-queued' },
            running: { label: 'running', className: 'status-running' },
            success: { label: 'success', className: 'status-success' },
            failed: { label: 'failed', className: 'status-failed' },
            error: { label: 'error', className: 'status-error' },
            cancelled: { label: 'cancelled', className: 'status-cancelled' },
            inconclusive: { label: 'inconclusive', className: 'status-inconclusive' }
        };

        const statusInfo = statusMap[status?.toLowerCase()] || { label: status, className: 'status-queued' };

        return (
            <span className={`status-badge ${statusInfo.className}`}>
                {statusInfo.label}
            </span>
        );
    };

    const getPlatformBadge = (platform) => {
        return (
            <span className={`platform-badge platform-${platform?.toLowerCase()}`}>
                {platform}
            </span>
        );
    };

    // const formatDuration = (startTime, endTime) => {
    //     if (!startTime || !endTime) return '-';

    //     const start = new Date(startTime);
    //     const end = new Date(endTime);
    //     const durationMs = end - start;

    //     if (durationMs < 1000) return `${durationMs}ms`;
    //     if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;

    //     const minutes = Math.floor(durationMs / 60000);
    //     const seconds = Math.round((durationMs % 60000) / 1000);
    //     return `${minutes}m ${seconds}s`;
    // };
    const formatDuration = (ms) => {
        if (!ms) return '-';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return minutes > 0
            ? `${minutes}m ${remainingSeconds}s`
            : `${seconds}s`;
    };
    const truncateId = (id) => {
        if (!id) return '-';
        return id.length > 8 ? `${id.substring(0, 8)}...` : id;
    };

    return (
        <div className="executions-page">
            <div className="executions-header">
                <h1>Execution History</h1>
            </div>

            {/* Filters */}
            <div className="executions-filters">
                <div className="search-box">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search by goal..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <select
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                    }}
                    className="filter-select"
                >
                    <option value="">All Statuses</option>
                    <option value="queued">Queued</option>
                    <option value="running">Running</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                    <option value="error">Error</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="inconclusive">Inconclusive</option>
                </select>
            </div>

            {/* Results Summary */}
            <div className="results-summary">
                {loading ? (
                    <span>Loading...</span>
                ) : (
                    <span>
                        Showing {executions.length} of {pagination.total} execution{pagination.total !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* Executions Table */}
            {loading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading executions...</p>
                </div>
            ) : executions.length === 0 ? (
                <div className="empty-state">
                    <Clock size={48} />
                    <h3>No executions found</h3>
                    <p>
                        {searchTerm || statusFilter
                            ? 'Try adjusting your filters'
                            : 'Run your first test to see execution history'}
                    </p>
                    {!searchTerm && !statusFilter && (
                        <Link to="/test-goals" className="btn btn-primary">
                            Create New Goal
                        </Link>
                    )}
                </div>
            ) : (
                <div className="executions-table-container">
                    <table className="executions-table">
                        <thead>
                            <tr>
                                {/* <th>ID</th> */}
                                <th>Goal</th>
                                <th>Platform</th>
                                <th>Status</th>
                                <th>Duration</th>
                                <th>Started</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {executions.map((execution) => (
                                <tr key={execution.id}>
                                    {/* <td className="execution-id">
                                        <code title={execution.id}>{truncateId(execution.id)}</code>
                                    </td> */}
                                    <td className="execution-name">
                                        <Link
                                            to={`/test/execution/${execution.id}`}
                                            title={execution.goalText || '-'}
                                        >{execution.goalText || '-'}
                                        </Link>
                                    </td>
                                    <td className="execution-platform">
                                        {execution.platform && getPlatformBadge(execution.platform)}
                                    </td>
                                    <td className="execution-status">
                                        {getStatusBadge(execution.status)}
                                    </td>
                                    <td className="execution-duration">
                                        <div className="duration-cell">
                                            <Clock size={14} />
                                            {formatDuration(execution.duration)}
                                        </div>
                                    </td>
                                    <td className="execution-time">
                                        {execution.createdAt ? <TimeAgo date={execution.createdAt} /> : '-'}
                                    </td>
                                    <td className="execution-actions">
                                        <div className="action-buttons">
                                            <Link
                                                to={`/test/execution/${execution.id}`}
                                                className="icon-btn"
                                                title="View Details"
                                            >
                                                <Eye size={16} />
                                            </Link>
                                            {execution.testGoalId && (
                                                <button
                                                    onClick={(e) => handleRerun(execution, e)}
                                                    className="icon-btn"
                                                    title="Rerun"
                                                >
                                                    <Play size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {!loading && executions.length > 0 && (
                <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    limit={pagination.limit}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    storageKey="rova_limit_history"
                />
            )}

            <Dialog {...dialogState} onClose={closeDialog} />
        </div>
    );
};

export default ExecutionHistory;
